import React from 'react';

/**
 * MealSwapModal [B14-D4]
 * Bir slot için makro-uyumlu alternatifleri gösterir (kalori/protein sapması + eksik kalem + maliyet).
 * Seçim → onSelect(altId). Tema diyet (yeşil) / sporcu (orange).
 */
const MEAL_LABEL = { breakfast: 'Kahvaltı', lunch: 'Öğle', dinner: 'Akşam' };

function deltaText(v, unit) {
  const n = Math.round(Number(v) || 0);
  if (n === 0) return `±0${unit}`;
  return `${n > 0 ? '+' : ''}${n}${unit}`;
}
function deltaCls(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) <= 50) return 'text-emerald-600 dark:text-emerald-400';
  return n > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400';
}

export default function MealSwapModal({
  open,
  onClose,
  loading = false,
  applying = false,
  alternatives = [],
  current = null,
  mealType = 'lunch',
  dateLabel = '',
  onSelect,
  theme = 'green'
}) {
  if (!open) return null;
  const accent = theme === 'orange'
    ? { ring: 'hover:border-orange-400 dark:hover:border-orange-500', btn: 'bg-orange-600 hover:bg-orange-700', chip: 'text-orange-600 dark:text-orange-400' }
    : { ring: 'hover:border-green-400 dark:hover:border-green-500', btn: 'bg-green-600 hover:bg-green-700', chip: 'text-green-600 dark:text-green-400' };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-[#0a0e17]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5 dark:border-white/10">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              {MEAL_LABEL[mealType] || mealType} · {dateLabel}
            </p>
            <h3 className="mt-1 text-xl font-black text-slate-900 dark:text-white">Yemeği Değiştir</h3>
            {current && (
              <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                Mevcut: <span className="font-bold">{current.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <div className="size-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 dark:border-slate-600 dark:border-t-slate-200" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Hedefe uyan alternatifler bulunuyor…</p>
            </div>
          ) : alternatives.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center dark:border-slate-700">
              <p className="text-sm font-semibold text-slate-400">Bu öğün için uygun alternatif bulunamadı.</p>
            </div>
          ) : (
            alternatives.map((alt) => (
              <button
                key={alt.id}
                type="button"
                disabled={applying}
                onClick={() => onSelect?.(alt.id)}
                className={`flex w-full items-center gap-3 rounded-2xl border-2 border-slate-200 bg-white p-3 text-left transition-all disabled:opacity-50 dark:border-white/10 dark:bg-white/5 ${accent.ring}`}
              >
                <div className="size-14 shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-white/10">
                  {alt.imageUrl ? (
                    <img src={alt.imageUrl} alt={alt.name} className="size-full object-cover" />
                  ) : (
                    <div className="flex size-full items-center justify-center text-slate-300">
                      <span className="material-symbols-outlined">restaurant</span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-black text-slate-900 dark:text-white">{alt.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold">
                    <span className="text-slate-600 dark:text-slate-300">{alt.resultCalories} kcal</span>
                    <span className={deltaCls(alt.calorieDelta)}>{deltaText(alt.calorieDelta, ' kcal')}</span>
                    <span className="text-slate-600 dark:text-slate-300">{alt.resultProtein}g prot</span>
                    <span className={`${accent.chip}`}>{alt.mealPortion}× porsiyon</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[10px] font-bold uppercase tracking-wider">
                    {alt.missingCount > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">+{alt.missingCount} eksik kalem</span>
                    ) : (
                      <span className="text-emerald-600 dark:text-emerald-400">Stokta var</span>
                    )}
                    {alt.estimatedCost ? (
                      <span className="text-slate-400">~{Math.round(alt.estimatedCost)} ₺</span>
                    ) : null}
                  </div>
                </div>
                <span className={`material-symbols-outlined shrink-0 ${accent.chip}`}>chevron_right</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
